import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { differenceInSeconds, format, getUnixTime, parseISO } from "date-fns";
import { BsPlayCircle, BsPauseCircle, BsFillChatLeftDotsFill } from "react-icons/bs";
import { IoCloseCircle } from "react-icons/io5";
import { differenceInDays } from "date-fns";
import cx from "classnames";

import "./RadioPlayer.css";
import { useChatActions, useChatStore } from "./store/chat";
import { usePopper } from "react-popper";
import { saveChatLastOpenedTime, savePopupLastShownTime } from "./util/popup";
import { ChatPopup } from "./ChatPopup/ChatPopup";
import { useInterval } from "./hooks/useInterval";
import { track } from "./util/mixpanel";

interface TrackInfo {
  title: string;
  startedAt: Date;
  endsAt: Date;
  nextTrack: Date;
  duration: number;
}

interface ProgressInfo {
  playedSoFar: number;
  remainingTime: number;
  percent: number;
}

interface P {
  hasChat?: boolean;
}

const audio = new Audio("https://www.radioking.com/play/radio-om-bun1");

export const RadioPlayer = ({ hasChat = true }: P) => {
  const chatState = useChatStore();
  const { showChat, setShowPopup } = useChatActions();
  const audioElement = useRef<HTMLAudioElement>(audio);
  const fetchingTrack = useRef<boolean>(false);

  const [chatButton, setChatButton] = useState<HTMLElement>();
  const [popupContainer, setPopupContainer] = useState<HTMLElement>();
  const [arrowElement, setArrowElement] = useState<HTMLElement>();

  const { styles, attributes } = usePopper(chatButton, popupContainer, {
    placement: "top-start",
    modifiers: [
      {
        name: "arrow",
        options: {
          element: arrowElement
        }
      }
    ]
  });

  const titleContainer = useRef<HTMLSpanElement>();

  const [paused, setPaused] = useState(true);
  const progressInfo = useRef<ProgressInfo>();
  const [trackInfo, setTrackInfo] = useState<TrackInfo>();

  // useEffect(() => {
  //   audioElement.current = ;
  //   if (paused === false) {
  //     setTimeout(() => {
  //       if (!audioElement.current) {
  //         return;
  //       }
  //       audioElement.current.load();
  //       audioElement.current.play();
  //     });
  //   }
  // }, []); // it's ok to run this only once in the begining

  const updateSessionPlayingStatus = useCallback(() => {
    if (paused) {
      return;
    }
    sessionStorage.setItem("ombun-player-time-seconds", getUnixTime(Date.now()).toString());
  }, [paused]);

  const updateProgressInfo = useCallback((trackInfo: TrackInfo) => {
    const { startedAt, endsAt, duration } = trackInfo;
    let playedSoFar = differenceInSeconds(new Date(), startedAt);

    let remainingTime = differenceInSeconds(endsAt, new Date());

    let percent = Math.round((playedSoFar / duration) * 100);
    if (percent > 100) {
      percent = 0;
    }
    if (playedSoFar > duration) {
      playedSoFar = duration;
    }
    if (remainingTime < 0) {
      remainingTime = 0;
    }

    progressInfo.current = {
      playedSoFar,
      remainingTime,
      percent
    };
  }, []);

  const fetchTrackInfo = useCallback(async () => {
    if (fetchingTrack.current) {
      return;
    }
    try {
      fetchingTrack.current = true;
      const response = await fetch(
        "https://api.radioking.io/widget/radio/radio-om-bun1/track/current"
      );
      const payload = await response.json();
      setTrackInfo({
        title: payload.title,
        startedAt: parseISO(payload.started_at),
        endsAt: parseISO(payload.end_at),
        nextTrack: parseISO(payload.next_track),
        duration: payload.duration
      });
      fetchingTrack.current = false;
    } catch {
      fetchTrackInfo();
    }
  }, []);

  const handleToggleClick = useCallback(async () => {
    if (paused) {
      audioElement.current.load();
      audioElement.current.play();
      setPaused(false);
      updateSessionPlayingStatus();
    } else {
      audioElement.current.pause();
      setPaused(true);
    }
  }, [paused, updateSessionPlayingStatus]);

  const handlePopupClose = useCallback(() => {
    setShowPopup(false);
  }, [setShowPopup]);
  const updateTrackProgress = useCallback(() => {
    if (fetchingTrack.current) {
      return;
    }

    if (trackInfo) {
      updateSessionPlayingStatus();
      updateProgressInfo(trackInfo);

      if (progressInfo.current?.remainingTime === 0) {
        fetchTrackInfo();
      }
    }
  }, [updateSessionPlayingStatus, updateProgressInfo, trackInfo, fetchTrackInfo]);

  useEffect(() => {
    const time = sessionStorage.getItem("ombun-player-time-seconds");
    if (time) {
      const parsedTime = Number.parseInt(time, 10);
      if (getUnixTime(Date.now()) - parsedTime < 60) {
        // duplicate code to play because this needs a different way of handling a possible error
        audioElement.current.load();
        audioElement.current.play().then(() => {
          if (audioElement.current.paused === false) {
            setPaused(false);
          }
        });
      }
    }
    fetchTrackInfo();
  }, [fetchTrackInfo]);

  useInterval(updateTrackProgress, 1000);

  useEffect(() => {
    if (chatState.showPopup) {
      savePopupLastShownTime();
    }
  }, [chatState.showPopup]);

  useEffect(() => {
    if (chatState.showPopup) {
      track("chatPopupShown");
    }
  }, []);

  useEffect(() => {
    if (trackInfo) {
      updateProgressInfo(trackInfo);
    }
  }, [trackInfo, updateProgressInfo]);

  const handleChatClick = useCallback(() => {
    saveChatLastOpenedTime();
    if (chatState.showPopup) {
      handlePopupClose();
    }
    showChat(!chatState.visible);
    if (!chatState.visible) {
      track("showChat");
    }
  }, [chatState, showChat, handlePopupClose]);

  useEffect(() => {
    if (!titleContainer.current) {
      return;
    }

    if (!trackInfo?.title) {
      return;
    }

    if (
      titleContainer.current.clientWidth > (titleContainer.current.parentElement?.clientWidth || 0)
    ) {
      titleContainer.current.classList.add("animate-title");
    } else {
      titleContainer.current.classList.remove("animate-title");
    }
  }, [trackInfo?.title]);

  return (
    <>
      {hasChat && (
        <ChatPopup
          showPopup={chatState.showPopup}
          popperStyles={styles.popper}
          arrowStyles={styles.arrow}
          setPopupRef={setPopupContainer}
          setArrowRef={setArrowElement}
          onCloseRequest={handlePopupClose}
          {...attributes}
        />
      )}
      <div className="fixed bottom-0 left-0 w-full h-20 lg:h-14">
        <div className="h-full w-full flex flex-row bg-gray justify-between">
          <div
            data-n="leftside"
            className=" flex flex-col  lg:flex-row justify-around items-center flex-1 py-2 min-w-0">
            <div className="flex flex-1 flex-col items-center w-[95%] lg:max-w-[70%] z-10">
              <div className="text-2xl max-w-[100%] overflow-hidden relative whitespace-nowrap text-center">
                <span
                  ref={titleContainer as any}
                  className="animate-title inline-block relative px-1">
                  {trackInfo && trackInfo.title}
                </span>
              </div>
            </div>
            <div className="flex-1 text-lg justify-center">
              <div>RADIO OM BUN</div>
              <div className="text-3xl px-1 leading-6">|</div>
              <div className="times">
                {trackInfo && format(trackInfo.startedAt, "HH:mm")}
                &nbsp;-&nbsp;
                {trackInfo && format(trackInfo.endsAt, "HH:mm")}
              </div>
            </div>
          </div>
          <div className="rightside z-20 flex-[0] min-w-[120px] p-2s flex">
            {hasChat && (
              <div
                ref={setChatButton as any}
                className="flex flex-1 items-center justify-center bg-a-purple cursor-pointer"
                onClick={handleChatClick}>
                <BsFillChatLeftDotsFill
                  className="relative top-[2px]"
                  color="white"
                  fontSize={24}
                />
              </div>
            )}
            <div
              className="flex flex-1 items-center justify-center bg-a-red cursor-pointer"
              onClick={handleToggleClick}>
              {paused ? (
                <BsPlayCircle color="white" fontSize={28} />
              ) : (
                <BsPauseCircle color="white" fontSize={28} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
